import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, FieldValues } from 'react-hook-form';
import { Alert, Grid2, Paper, Stack, Typography, Divider } from '@mui/material';
import { FormField, FormFieldType } from '@enroller/admissions-components';
import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext.tsx';
import { getConfig } from '../../../utils/config.ts';
import { usePageTitle } from '@enroller/utils';
import { defaultValues, PersonalReferencesFormValues, personalReferencesSchema } from './PersonalReferencesFormValues.ts';
import { zodResolver } from '@hookform/resolvers/zod';

const { googleMapsAPIKey } = getConfig();
const relationshipOptions = [
  {
    value: 'Family Friend',
  },
  {
    value: 'Neighbor',
  },
  {
    value: 'Professional',
  },
  {
    value: 'Other',
  },
];
interface PersonalReferencesFormProps {
  applicationData?: FieldValues;
  submitApplication: (data: FieldValues) => void;
}

export const PersonalReferences = ({ applicationData }: PersonalReferencesFormProps) => {
  const { setSubmit, setHeaderTitle } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');

  useEffect(() => {
    setHeaderTitle({ title: 'Personal References', subtitle: 'Reference 1' });
    setPageTitle('Personal References');
  }, [setHeaderTitle, setPageTitle]);

  // Define resolver based on validation mode needed
  const resolver = useMemo(
    () => zodResolver(validationMode === ValidationType.Full ? personalReferencesSchema.required : personalReferencesSchema.optional),
    [validationMode],
  );

  const { control, handleSubmit, reset } = useForm<PersonalReferencesFormValues>({ defaultValues, resolver });

  useEffect(() => {
    if (applicationData) {
      reset({
        ...defaultValues,
        ...applicationData,
      });
    }
  }, [applicationData, reset]);

  const onSubmit = useCallback(
    () =>
      handleSubmit((data) => {
        // Format data
        console.log('Form submitted: ', data);
      })(),
    [handleSubmit],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  return (
    <Stack component="form" onSubmit={onSubmit} gap={2}>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={3}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Personal References</Typography>
          </Grid2>

          <Grid2 size={12}>
            <Alert sx={{ alignItems: 'flex-start' }} variant="outlined" severity="info">
              Please provide 2 personal references as required by the U.S. Department of State regulation: § 62.25 (j)(5) Ensure that the host family has a good
              reputation and character by securing two personal references from within your community from individuals who are not relatives of the potential
              host family or representatives of the sponsor (i.e., field staff or volunteers), attesting to the host family’s good reputation and character. The
              department is looking for references from individuals who know the family as a unit; and who have actually been in the family home within the last
              year.
            </Alert>
          </Grid2>

          <Grid2 size={12}>
            <Typography variant="subtitle1">Reference 1</Typography>
          </Grid2>
          <Grid2 size={12}>
            <Paper sx={{ p: 2 }} variant="outlined">
              <Grid2 container wrap="wrap" spacing={{ xs: 2, md: 4 }}>
                <Grid2 size={{ xs: 12, md: 4 }}>
                  <FormField type={FormFieldType.Text} label="First Name" name="reference1.firstName" control={control} />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 4 }}>
                  <FormField type={FormFieldType.Text} label="Last Name" name="reference1.lastName" control={control} />
                </Grid2>

                <Grid2 size={{ xs: 0, md: 4 }} />
                <Grid2 size={12}>
                  <FormField
                    control={control}
                    googleMapsAPIKey={googleMapsAPIKey}
                    label="Residential Address"
                    name="reference1.address"
                    type={FormFieldType.AddressAutocomplete}
                    restrictToUS
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 4 }}>
                  <FormField type={FormFieldType.Text} label="Reference's Phone Number" name="reference1.phoneNumber" control={control} />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 4 }}>
                  <FormField type={FormFieldType.Text} label="Reference's Email" name="reference1.email" control={control} />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 4 }}>
                  <FormField
                    type={FormFieldType.Dropdown}
                    options={relationshipOptions}
                    label="Relationship with family"
                    name="reference1.relationship"
                    control={control}
                  />
                </Grid2>
              </Grid2>
            </Paper>
          </Grid2>

          <Grid2 size={12}>
            <Paper sx={{ p: 2 }} variant="outlined">
              <Grid2 container wrap="wrap" spacing={{ xs: 2, md: 4 }}>
                <Grid2 size={{ xs: 12, md: 4 }}>
                  <FormField type={FormFieldType.Text} label="First Name" name="reference2.firstName" control={control} />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 4 }}>
                  <FormField type={FormFieldType.Text} label="Last Name" name="reference2.lastName" control={control} />
                </Grid2>

                <Grid2 size={{ xs: 0, md: 4 }} />
                <Grid2 size={12}>
                  <FormField
                    control={control}
                    googleMapsAPIKey={googleMapsAPIKey}
                    label="Residential Address"
                    name="reference2.address"
                    type={FormFieldType.AddressAutocomplete}
                    restrictToUS
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 4 }}>
                  <FormField type={FormFieldType.Text} label="Reference's Phone Number" name="reference2.phoneNumber" control={control} />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 4 }}>
                  <FormField type={FormFieldType.Text} label="Reference's Email" name="reference2.email" control={control} />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 4 }}>
                  <FormField
                    type={FormFieldType.Dropdown}
                    options={relationshipOptions}
                    label="Relationship with family"
                    name="reference2.relationship"
                    control={control}
                  />
                </Grid2>
              </Grid2>
            </Paper>
          </Grid2>

          <Grid2 size={12} sx={{ py: 2 }}>
            <Divider variant="fullWidth" />
          </Grid2>

          <Grid2 size={12}>
            <Typography variant="subtitle1">Reference 2</Typography>
          </Grid2>
        </Grid2>
      </Paper>
    </Stack>
  );
};
